import {
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';

import ProjectsViewTypeListIcon from 'common/shared-ui/src/icons/ProjectsViewTypeListIcon';
import ProjectsViewTypeTilesIcon from 'common/shared-ui/src/icons/ProjectsViewTypeTilesIcon';
import { AIAgent } from 'components/AIAgent';
import { useAppSelector } from 'components/app/redux/store';

import {
  deleteSavedQuery,
  editQuery,
  getProjectsFilters,
  getProjectsList,
  getSavedQuery,
  getUserCanCreate,
  saveQuery,
} from '../../../packages/common/api';
import ChatBot from '../../../packages/common/shared-ui/src/components/ChatBot';
import WarningModal from '../../../packages/common/shared-ui/src/components/WarningModal';
import { ButtonsTypes } from '../../../packages/common/shared-ui/src/enums/commonEnums';
import { useTranslation } from '../../../packages/common/utils/getLocalString';
import {
  formatFilters,
  formatQueryFilters,
  formatQueryToSave,
  paramsToQuery,
  queryToParams,
  selectFilters,
} from '../../../packages/common/utils/gridFiltersHelperNew';
import CommonPage from '../CommonPage/CommonPage';
import GridFiltersModalNew from '../GridFiltersModalNew/GridFiltersModalNew';
import GridFiltersPanelNew from '../GridFiltersPanelNew/GridFiltersPanelNew';
import GridSaveQueryModal from '../GridSaveQueryModal/GridSaveQueryModal';

import ProjectsListView from './ProjectsListView';
import ProjectsTilesView from './ProjectsTilesView';

// Original class: ProjectsViewChangeButtonWrap
const StyledProjectsViewChangeButtonWrap = styled(ToggleButtonGroup)(theme => ({
  float: 'right',
  position: 'relative',
}));
// Original class: ProjectsViewChangeButton
const StyledProjectsViewChangeButton = styled(ToggleButton)(theme => ({
  border: '0px !important',
  padding: '0px !important',
}));
// Original class: fancyScroll
const StyledFancyScroll = styled(Grid)(theme => ({
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#E4E4EF',
  },

  '&::-webkit-scrollbar': {
    width: '4px',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#7174AC',
    borderRadius: '5px',
  },
}));
const ProjectsContainer = () => {
  const getLocalString = useTranslation();
  const featureFlags = useAppSelector(state => state.configuration.features);
  const navigate = useNavigate();
  const [queryModalState, setQueryModalState] = useState(null);
  const pageUseParams = useParams();
  const [openParamsModal, setOpenParamsModal] = useState(false);
  const [openSaveQueryModal, setOpenSaveQueryModal] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [queryData, setQueryData] = useState(null);
  const [currentParams, setCurrentParams] = useState(null);
  const [initialParams, setInitialParams] = useState(null);
  const [view, setView] = React.useState(
    window.localStorage.getItem('projectsView') || 'tiles',
  );
  const [loading, setLoading] = useState(true);
  const [paramsLoading, setParamsLoading] = useState(false);
  const [gridRows, setGridRows] = useState([]);
  const [gridColumns, setGridColumns] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const [query, setQuery] = useQueryParams({
    filters: StringParam,
    sorts: StringParam,
    fields: StringParam,
    treeView: StringParam,
  });

  const handleError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );

  useEffect(() => {
    let ignore = false;
    let params = {};
    pageUseParams.queryId && (params.query = Number(pageUseParams.queryId));
    async function getProjectsFiltersData() {
      const response = await getProjectsFilters(params);
      if (!ignore) {
        return response;
      }
    }

    getProjectsFiltersData()
      .then(res => {
        const { data: filtersData } = res;
        const params = formatFilters(filtersData);
        !initialParams && setInitialParams(params);
      })
      .catch(error => handleError(error.response));
    return () => {
      ignore = true;
    };
  }, [handleError, pageUseParams.queryId]);

  useEffect(() => {
    let ignore = false;
    let params = {};
    setParamsLoading(true);
    let queryIsSet = false;
    queryIsSet =
      Object.values(query).filter(item => item !== undefined).length > 0;
    queryIsSet && (params = { ...params, ...query });
    async function getProjectsData() {
      const response = await getProjectsList(params);
      if (!ignore) {
        return response;
      }
    }
    if (queryIsSet) {
      getProjectsData()
        .then(res => {
          const { data: projectsData } = res;
          projectsData.fields && setGridColumns(projectsData.fields);
          projectsData.rows && setGridRows(projectsData.rows);
          setLoading(false);
          setParamsLoading(false);
        })
        .catch(error => handleError(error.response));
    }
    return () => {
      ignore = true;
    };
  }, [handleError, query]);

  //Получение query по id
  useEffect(() => {
    setQueryData(null);
    let ignore = false;
    async function getSavedQueries() {
      const { data: response } = await getSavedQuery({
        id: pageUseParams.queryId,
      });
      if (!ignore) {
        return response;
      }
    }
    if (pageUseParams.queryId) {
      getSavedQueries()
        .then(res => {
          res && setQueryData(res);
        })
        .catch(error => handleError(error.response));
    }
  }, [handleError, pageUseParams.queryId]);

  useEffect(() => {
    if (!pageUseParams.queryId && initialParams) {
      if (Object.values(query).every(x => x === undefined)) {
        setQuery(paramsToQuery(initialParams));
        setCurrentParams(initialParams);
      } else {
        setCurrentParams(queryToParams(query, initialParams));
      }
    }
  }, [initialParams, pageUseParams.queryId, setQuery]);
  useEffect(() => {
    if (pageUseParams.queryId && initialParams && queryData) {
      if (Object.values(query).every(x => x === undefined)) {
        setQuery(paramsToQuery(initialParams, queryData.query));
        setCurrentParams(formatQueryFilters(initialParams, queryData.query));
      } else {
        setCurrentParams(queryToParams(query, initialParams));
      }
    }
  }, [
    initialParams,
    pageUseParams.queryId,
    setQuery,
    queryData,
    setCurrentParams,
  ]);

  useEffect(() => {
    let ignore = false;

    async function getCanCreate() {
      const { data: response } = await getUserCanCreate();
      if (!ignore) {
        return response;
      }
    }

    getCanCreate()
      .then(res => {
        setCanCreate(res);
      })
      .catch(error => handleError(error.response));

    return () => {
      ignore = true;
    };
  }, [handleError]);

  useEffect(() => {
    if (
      currentParams &&
      currentParams.sorts &&
      currentParams.sorts.selectedItems
    ) {
      setSortModel(
        currentParams.sorts.selectedItems.map(item => ({
          field: item.fieldId,
          sort: item.direction,
        })),
      );
    }
  }, [currentParams]);

  const handleSelectFilters = useCallback(
    data => {
      const result = selectFilters(data, currentParams);
      setCurrentParams(result);
      setQuery(paramsToQuery(result));
    },
    [currentParams, setQuery],
  );

  const handleParamsModalSave = useCallback(
    data => {
      setCurrentParams(data);
      setQuery(paramsToQuery(data));
      setOpenParamsModal(false);
    },
    [setQuery],
  );

  const handleSaveQueryModalSave = useCallback(
    async modalData => {
      const value = formatQueryToSave(modalData, currentParams, 'project');
      try {
        setSaveLoader(true);
        let response;
        if (queryModalState === 'edit') {
          response = await editQuery(pageUseParams.queryId, value);
        } else {
          response = await saveQuery(value);
        }
        if (response) {
          setSaveLoader(false);
          const { data: res } = response;
          window.open(`/projects/query/${res.id}`, '_self');
        }
      } catch (error) {
        handleError(error.response);
      }
      setOpenSaveQueryModal(false);
    },
    [currentParams, handleError, pageUseParams.queryId, queryModalState],
  );

  const handleChangeView = (event, nextView) => {
    setView(nextView);
    window.localStorage.setItem('projectsView', nextView);
  };

  const ViewToggle = () => {
    return (
      <StyledProjectsViewChangeButtonWrap
        orientation='vertical'
        value={view}
        exclusive
        onChange={handleChangeView}
      >
        {view !== 'list' ? (
          <StyledProjectsViewChangeButton value='list' aria-label='list'>
            <ProjectsViewTypeListIcon
              width={32}
              height={32}
              viewBox='0 0 32 32'
            />
          </StyledProjectsViewChangeButton>
        ) : null}
        {view !== 'tiles' ? (
          <StyledProjectsViewChangeButton value='tiles' aria-label='tiles'>
            <ProjectsViewTypeTilesIcon
              width={32}
              height={32}
              viewBox='0 0 32 32'
            />
          </StyledProjectsViewChangeButton>
        ) : null}
      </StyledProjectsViewChangeButtonWrap>
    );
  };

  const handleParamsModalClose = useCallback(() => {
    setOpenParamsModal(false);
  }, []);
  const handleSaveQueryModalClose = useCallback(() => {
    setOpenSaveQueryModal(false);
  }, []);
  const handleButtonEvent = useCallback(
    type => {
      switch (type) {
        case ButtonsTypes.ADD:
          const projectNewUrl = '/projects/create';
          navigate(projectNewUrl);
          break;
        case ButtonsTypes.PARAMS:
          setOpenParamsModal(true);
          break;
        case ButtonsTypes.QUERY:
          setQueryModalState('create');
          setOpenSaveQueryModal(true);
          break;
        case ButtonsTypes.EDIT_QUERY:
          setQueryModalState('edit');
          setOpenSaveQueryModal(true);
          break;
        case ButtonsTypes.DELETE_QUERY:
          setOpenWarningModal(true);
          break;
      }
    },
    [navigate],
  );
  const handleWarningModalClose = useCallback(() => {
    setOpenWarningModal(false);
  }, []);
  const handleToggleDelete = useCallback(
    async e => {
      switch (e.currentTarget.attributes.value.value) {
        case 'save':
          await deleteSavedQuery({ id: pageUseParams.queryId });
          navigate('/projects');
          setOpenWarningModal(false);
          break;
        case 'delete':
          setOpenWarningModal(false);
          break;
      }
    },
    [pageUseParams.queryId, navigate],
  );
  const handleChangeSorting = useCallback(
    newSortModel => {
      if (!newSortModel || newSortModel.length === 0) {
        setQuery(prev => ({ ...prev, sorts: undefined }));
        return;
      }

      const sortString = newSortModel
        .map(item => `${item.field};${item.sort}`)
        .join('$$');

      setQuery(prev => ({ ...prev, sorts: sortString }));
    },
    [setQuery],
  );
  return (
    <>
      <WarningModal
        simple
        isWarningModalOpen={openWarningModal}
        handleToggle={handleToggleDelete}
        closeWarningModal={handleWarningModalClose}
        title={`${getLocalString('messages', 'deleteQuery')}?`}
      />
      {currentParams && (
        <GridFiltersModalNew
          handleSave={data => handleParamsModalSave(data)}
          handleClose={handleParamsModalClose}
          open={openParamsModal}
          data={currentParams}
        />
      )}
      <GridSaveQueryModal
        state={queryModalState}
        permission={'canSaveProjectsGlobalQueries'}
        queryData={queryData}
        open={openSaveQueryModal}
        handleSave={data => handleSaveQueryModalSave(data)}
        handleClose={handleSaveQueryModalClose}
        saveLoader={saveLoader}
      />
      <CommonPage
        loading={loading}
        buttons={<ViewToggle />}
        canCreate={canCreate}
        buttonEvent={handleButtonEvent}
        queryName={queryData?.name}
        queryUserId={queryData?.userId}
        component={'ProjectsContainer'}
      >
        {currentParams && currentParams.filters?.selectedItems?.length > 0 && (
          <Box sx={{ marginTop: '10px' }}>
            <GridFiltersPanelNew
              data={currentParams.filters.selectedItems}
              handleCloseFilter={handleSelectFilters}
            ></GridFiltersPanelNew>
          </Box>
        )}
        <StyledFancyScroll
          container
          wrap='nowrap'
          sx={{
            overflow: 'auto',
            flexDirection: 'column',
            marginTop: '20px',
          }}
        >
          {paramsLoading && (
            <Grid
              container
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                zIndex: 99,
                position: 'fixed',
                top: 0,
                left: 0,
              }}
            >
              <CircularProgress color='secondary' />
            </Grid>
          )}
          {featureFlags.CHATBOT &&
            (featureFlags.CHATBOT_VOICE_INPUT ? <AIAgent /> : <ChatBot />)}
          {view === 'tiles' && (
            <ProjectsTilesView
              projects={gridRows}
              projectscColumns={gridColumns}
            ></ProjectsTilesView>
          )}
          {view === 'list' && (
            <ProjectsListView
              projects={gridRows}
              projectscColumns={gridColumns}
              changeSorting={handleChangeSorting}
            ></ProjectsListView>
          )}
        </StyledFancyScroll>
      </CommonPage>
    </>
  );
};
export default ProjectsContainer;
